<i18n>
{
  "fr": {
    "projects-list-title": "Conformité par projet",
    "projects-list-table-title": "Projets ({count})",
    "add-project-btn": "Ajouter un projet",
    "generate-manual-btn": "Générer le manuel",
    "delete-project-title": "Supprimer le projet",
    "delete-project-message": "Êtes-vous certain de vouloir supprimer le projet «\u00A0{project}\u00A0»\u00A0? Toutes les personnes assignées, les notes de suivi inscrites ou les documents précisés seront perdus.",
    "delete-project-cancel": "Annuler",
    "delete-project-confirm": "Supprimer"
  },
  "en": {
    "projects-list-title": "Project compliance",
    "projects-list-table-title": "Projects ({count})",
    "add-project-btn": "Add a project",
    "generate-manual-btn": "Generate manual",
    "delete-project-title": "Delete project",
    "delete-project-message": "Are you sure that you want to delete the project \"{project}\"? All assignments, notes and documents will be lost.",
    "delete-project-cancel": "Cancel",
    "delete-project-confirm": "Delete"
  }
}
</i18n>

<template>
  <div>
    <prismic-content :doctype="DOCUMENT_TYPE_PROJECTS_LIST" v-slot="{ content }">
      <ui-title>
        {{ $t('projects-list-title') }}
        <template v-slot:description>
          <prismic-rich-text :field="content.intro" />
        </template>
      </ui-title>

      <div class="card">
        <ui-box>
          <hr class="is-marginless" />
          <div v-for="(drawer, index) of content.drawers" :key="index">
            <ui-drawer>
              <template slot="title">{{ $prismicText(drawer.drawer_title) }}</template>
              <prismic-rich-text class="content is-custom has-max-width-768" :field="drawer.drawer_content" />
            </ui-drawer>
            <hr class="is-marginless" />
          </div>
        </ui-box>
      </div>
    </prismic-content>

    <project-template-list
      v-if="projectTemplatesData"
      :loading="$apollo.queries.businessProjectTemplates.loading"
      :project-templates="projectTemplatesData"
    />

    <div class="card mt-12px">
      <ui-box>
        <div class="level mb-24">
          <div class="level-left" style="flex-shrink: 1">
            <h2 class="title is-4 has-text-weight-bold is-marginless">
              {{ $t('projects-list-table-title', { count: projectsTotalCount }) }}
            </h2>
          </div>
          <div class="level-right">
            <b-button type="is-link" outlined @click="addProject" icon-left="plus-circle" class="mr-2">{{
              $t('add-project-btn')
            }}</b-button>
            <b-button
              v-if="isAdmin"
              type="is-link"
              outlined
              tag="router-link"
              :to="{ name: $consts.urls.URL_MANUAL_GENERATION }"
              icon-left="rocket"
            >
              {{ $t('generate-manual-btn') }}
            </b-button>
          </div>
        </div>
        <project-list-table
          v-if="projectsData"
          :loading="$apollo.queries.businessProjects.loading"
          :projects="projectsData"
          :currentPage="currentPage"
          :perPage="PROJECT_LIST_LIMIT"
          :sortField="sortField"
          :sortOrder="sortOrder"
          :totalCount="projectsTotalCount"
          :totalPages="projectsTotalPages"
          @pageChange="onPageChange"
          @sortChange="onSortChange"
          @deleteProject="onDeleteProject"
          class="mt-16px"
        />
      </ui-box>
    </div>
  </div>
</template>

<script>
import BusinessService from '@/services/business'

import Breadcrumbs from '@/mixins/breadcrumbs'
import { URL_PROJECT_ADD, URL_PROJECT_COMPLIANCE } from '@/consts/urls'
import { DOCUMENT_TYPE_PROJECTS_LIST } from '@/consts/prismic'
import {
  BUSINESS_USER_TYPE_BUSINESS_ADMIN,
  PROJECT_SORT_DEFAULT,
  PROJECT_SORT_NAME,
  PROJECT_SORT_JURISDICTION_CODE,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from '@/consts/enums'

import ProjectListTable from '@/components/projects/project-list-table'
import ProjectTemplateList from '@/components/projects/project-template-list'
import PrismicContent from '@/components/prismic/prismic-content'
import UiBox from '@/components/ui/box'
import UiDrawer from '@/components/ui/drawer'
import UiTitle from '@/components/ui/title'

const PROJECT_LIST_LIMIT = 12
const PROJECT_TEMPLATE_LIST_LIMIT = 100

export default {
  mixins: [Breadcrumbs],
  components: {
    ProjectListTable,
    ProjectTemplateList,
    PrismicContent,
    UiBox,
    UiDrawer,
    UiTitle
  },
  data() {
    return {
      DOCUMENT_TYPE_PROJECTS_LIST,
      businessUserType: null,
      currentPage: 1,
      sortField: this.$route.query.s || 'status',
      sortOrder: this.$route.query.o || 'asc',
      URL_PROJECT_COMPLIANCE,
      PROJECT_LIST_LIMIT,
      PROJECT_TEMPLATE_LIST_LIMIT
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('projects-list-title')
        }
      ]
    },
    isAdmin() {
      return this.businessUserType === BUSINESS_USER_TYPE_BUSINESS_ADMIN
    },
    currentBusinessId() {
      const business = BusinessService.getCurrentBusiness()
      if (!business) return null

      return business.id
    },
    projectsData() {
      return this.businessProjects ? this.businessProjects.items : null
    },
    projectTemplatesData() {
      return this.businessProjectTemplates ? this.businessProjectTemplates : []
    },
    projectsTotalCount() {
      return this.businessProjects ? this.businessProjects.totalCount : 0
    },
    projectsTotalPages() {
      return this.businessProjects ? this.businessProjects.totalPages : 0
    },
    projectsSortField() {
      switch (this.sortField) {
        case 'name':
          return PROJECT_SORT_NAME
        case 'jurisdictionCode':
          return PROJECT_SORT_JURISDICTION_CODE
        default:
          return PROJECT_SORT_DEFAULT
      }
    },
    projectsSortOrder() {
      switch (this.sortOrder) {
        case 'desc':
          return SORT_ORDER_DESC
        default:
          return SORT_ORDER_ASC
      }
    }
  },
  methods: {
    addProject: function () {
      this.$router.push({ name: URL_PROJECT_ADD })
    },
    onPageChange({ page }) {
      this.currentPage = page
    },
    onSortChange({ field, order }) {
      this.sortField = field
      this.sortOrder = order
      this.currentPage = 1

      this.$router.replace({ query: { s: field, o: order } })
    },
    onDeleteProject({ project }) {
      this.$buefy.dialog.confirm({
        title: this.$t('delete-project-title'),
        message: this.$t('delete-project-message', { project: project.name }),
        cancelText: this.$t('delete-project-cancel'),
        confirmText: this.$t('delete-project-confirm'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await this.$apollo.mutate({
            mutation: this.$options.mutation.DeleteProject,
            variables: {
              projectId: project.id
            }
          })

          this.$success()

          await this.$apollo.queries.businessProjects.refetch()
        }
      })
    }
  },
  apollo: {
    userType: {
      query() {
        return this.$options.query.UserType
      },
      variables() {
        return {
          businessId: this.currentBusinessId
        }
      },
      update({
        me: {
          userBusiness: [{ userType }]
        }
      }) {
        this.businessUserType = userType
      },
      fetchPolicy: 'cache-first',
      skip() {
        return !this.currentBusinessId
      }
    },
    businessProjects: {
      query() {
        return this.$options.query.businessProjects
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          page: this.currentPage,
          sort: this.projectsSortField,
          order: this.projectsSortOrder,
          limit: PROJECT_LIST_LIMIT
        }
      },
      skip() {
        return !this.currentBusinessId
      }
    },
    businessProjectTemplates: {
      query() {
        return this.$withFragments(
          this.$options.query.businessProjectTemplates,
          ProjectTemplateList.fragments.projectTemplatesList
        )
      },
      variables() {
        return {
          businessId: this.currentBusinessId
        }
      },
      skip() {
        return !this.currentBusinessId
      }
    }
  }
}
</script>

<graphql>
query UserType($businessId: ID!) {
  me {
    id
    userBusiness(businessId: { value: $businessId }) {
      id
      userType
    }
  }
}

query businessProjects($businessId: ID!, $page: Int!, $limit: Int!, $sort: ProjectSort!, $order: SortOrder!) {
  businessProjects: searchProjects(businessId: $businessId, page: $page, limit: $limit, sort: $sort, order: $order) {
    items {
      id
      name
      jurisdictionCode
      jurisdictionOther
      status
      stats {
        directIndicatorsCount
        directDocumentsCount
        directDocumentsCompletedCount
      }
    }
    pageNumber
    pageSize
    totalCount
    totalPages
  }
}
query businessProjectTemplates($businessId: ID!) {
  businessProjectTemplates: projectTemplates(businessId: $businessId) {
    id
    name
    status
    ...projectTemplatesList
  }
}
mutation DeleteProject($projectId: ID!) {
  deleteProject(input: { projectId: $projectId }) {
    success
  }
}
</graphql>
